import React, { useContext, useEffect, useState, useRef } from "react";

import CloseIcon from "@mui/icons-material/Close";
import { LoadingButton } from "@mui/lab";
import {
  Drawer,
  Typography,
  Box,
  IconButton,
  CircularProgress,
  Button,
  TextField,
  Chip,
} from "@mui/material";
import {
  SubmissionSummary,
  SubmissionAttachment,
  GetSubmissionResponsePortal,
  getSurvey,
  patchSubmissionPortal,
} from "@rubi/api-generated";
import { toast } from "@rubi/ui";
import { useNavigate, useParams } from "react-router-dom";
import { Model } from "survey-core";

import { OnlineReportingContext } from "@/components/online-reporting/context";
import CondensedReportSurvey from "@/components/online-reporting/report-view/CondensedReportSurvey";
import {
  applyTranslation,
  hasForeignLocale,
} from "@/components/online-reporting/report-view/translation";
import TranslationToggle from "@/components/online-reporting/report-view/TranslationToggle";
import {
  getTitleFromSubmission,
  createCondensedModel,
} from "@/components/online-reporting/utils";
import { APP_BAR_HEIGHT } from "@/layout/AppBar";
import { reportColourTypes, statusTypes } from "@/types/typesReportV2";
import {
  fetchSubmissionData,
  parseMediaDetails,
  type MediaDetail,
} from "@/utils/submissionUtils";

interface RegistryDetailsPanelProps {
  open: boolean;
  onClose: () => void;
  registryItem?: SubmissionSummary;
  loading?: boolean;
}

const RegistryDetailsPanel: React.FC<RegistryDetailsPanelProps> = ({
  open,
  onClose,
  registryItem,
  loading: initialLoading = false,
}) => {
  const [loading, setLoading] = useState(initialLoading);
  const [fullSubmission, setFullSubmission] =
    useState<GetSubmissionResponsePortal | null>(null);
  const [attachments, setAttachments] = useState<SubmissionAttachment[]>([]);
  const [mediaDetails, setMediaDetails] = useState<MediaDetail[]>([]);
  const navigate = useNavigate();
  const { orgId } = useParams();
  const { getSubmissionForID } = useContext(OnlineReportingContext);
  const [notesModified, setNotesModified] = useState(false);
  const [saveNotesLoading, setSaveNotesLoading] = useState(false);
  const notesRef = useRef<HTMLInputElement>();
  const [surveyModel, setSurveyModel] = useState<Model>();
  const [pdfConfiguration, setPdfConfiguration] = useState<any>();
  const [showTranslated, setShowTranslated] = useState(true);

  const buildModel = async (
    details: GetSubmissionResponsePortal,
    configuration: any,
    translated: boolean,
  ) => {
    const { newSurvey } = await createCondensedModel(
      configuration,
      (translated &&
        applyTranslation(details.submission, details.translation_en)) ||
        details.submission,
      details.attachments ?? [],
      true, // hideAdminInformation
      false, // isSupplementary
      false, // printVersion
      details.submission_id, // submissionID
    );
    setSurveyModel(newSurvey);
  };

  const handleTranslationToggle = async (translated: boolean) => {
    if (!fullSubmission || translated === showTranslated) return;
    await buildModel(fullSubmission, pdfConfiguration, translated);
    setShowTranslated(translated);
  };

  useEffect(() => {
    const loadSubmissionData = async () => {
      if (!registryItem?.id) return;

      setLoading(true);
      try {
        const { details, attachments } = await fetchSubmissionData(
          registryItem.id,
          getSubmissionForID,
        );
        setFullSubmission(details);
        setAttachments(attachments);
        setMediaDetails(parseMediaDetails(attachments));

        // Create condensed survey model
        if (details) {
          const survey = (
            await getSurvey({ path: { survey_id: details.survey_id } })
          ).data!;
          setPdfConfiguration(survey.survey.pdf_configuration);
          setShowTranslated(true);
          await buildModel(details, survey.survey.pdf_configuration, true);
        }
      } catch (error) {
        console.error("Error loading submission data:", error);
      }
      setLoading(false);
    };

    loadSubmissionData();
  }, [registryItem?.id, getSubmissionForID]);

  const updateNotes = async () => {
    if (!registryItem?.id) return;

    setSaveNotesLoading(true);
    try {
      await patchSubmissionPortal({
        path: { submission_id: registryItem.id },
        body: {
          notes: notesRef?.current?.value ?? "",
        },
      });

      setFullSubmission((prev) => ({
        ...prev!,
        notes: notesRef?.current?.value ?? "",
      }));

      setNotesModified(false);
      toast.success("Notes updated successfully");
    } catch (error) {
      toast.error("Failed to update notes, please try again later");
    }
    setSaveNotesLoading(false);
  };

  if (!registryItem && !loading) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: "40%",
          minWidth: "400px",
          maxWidth: "800px",
          height: `calc(100vh - ${APP_BAR_HEIGHT}px)`, // Account for app bar height
          top: `${APP_BAR_HEIGHT}px`, // Start below app bar
          bottom: 0,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
            borderBottom: 1,
            borderColor: "divider",
            flexShrink: 0,
            backgroundColor: "background.paper",
          }}
        >
          <Typography variant="h6">Registry Entry Details</Typography>
          <IconButton onClick={onClose} size="large">
            <CloseIcon />
          </IconButton>
        </Box>

        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexGrow: 1,
            }}
          >
            <CircularProgress />
          </Box>
        ) : registryItem ? (
          <Box
            sx={{
              flexGrow: 1,
              overflowY: "auto",
              overflowX: "hidden",
              p: 2,
              pb: 4, // Add extra padding at bottom for better scrolling
            }}
          >
            {/* Internal Notes Section */}
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Internal Private Notes
            </Typography>
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                multiline
                minRows={4}
                inputRef={notesRef}
                defaultValue={fullSubmission?.notes ?? ""}
                onChange={() => {
                  if (!notesModified) {
                    setNotesModified(true);
                  }
                }}
                variant="outlined"
              />
              <LoadingButton
                variant="contained"
                color="info"
                size="medium"
                fullWidth
                sx={{ mt: 2 }}
                onClick={updateNotes}
                loading={saveNotesLoading}
                disabled={!notesModified}
              >
                Save Changes
              </LoadingButton>
            </Box>

            {/* Status Information */}
            <Box sx={{ mb: 3 }}>
              <Typography
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <strong>Status:</strong>{" "}
                <Chip
                  style={{
                    opacity:
                      registryItem.status === "rejected" ||
                      registryItem.status === "approved"
                        ? 0.6
                        : 1,
                  }}
                  label={
                    statusTypes[registryItem.status]
                      ? statusTypes[registryItem.status]
                      : registryItem.status
                  }
                  color={
                    reportColourTypes[registryItem.status]
                      ? reportColourTypes[registryItem.status]
                      : "default"
                  }
                />
              </Typography>
            </Box>

            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {registryItem.survey_name}
            </Typography>

            {/* Title */}
            {fullSubmission && (
              <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
                {getTitleFromSubmission(
                  fullSubmission,
                  registryItem.survey_name ?? "",
                )}
              </Typography>
            )}

            {/* Condensed Survey Form — the shared renderer, so the panel gets
                the same .condensed-report styling (light and dark) as the
                report view. It expects an outer card, drawn here. */}
            {surveyModel && (
              <Box
                sx={{
                  mb: 3,
                  p: 3,
                  bgcolor: "var(--cr-card-bg)",
                  borderRadius: 1,
                  boxShadow: 1,
                }}
              >
                {hasForeignLocale(fullSubmission?.locale) && (
                  <TranslationToggle
                    locale={fullSubmission!.locale!}
                    showTranslated={showTranslated}
                    onChange={handleTranslationToggle}
                  />
                )}
                <CondensedReportSurvey model={surveyModel} />
              </Box>
            )}

            {/* Media Details */}
            {mediaDetails.length > 0 && (
              <>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Media Details
                </Typography>
                <Box sx={{ mb: 3 }}>
                  {mediaDetails.map((detail) => (
                    <Box key={detail.url} sx={{ mb: 2 }}>
                      {detail.media_type.startsWith("image") ? (
                        <img
                          src={detail.url}
                          alt="Submission attachment"
                          style={{ maxWidth: "100%", height: "auto" }}
                        />
                      ) : detail.media_type.startsWith("video/") ? (
                        <video
                          controls
                          style={{ maxWidth: "100%", height: "auto" }}
                        >
                          <source src={detail.url} type={detail.media_type} />
                          Your browser does not support the video tag.
                        </video>
                      ) : (
                        <Typography>
                          <strong>{detail.url}</strong> - {detail.media_type}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </Box>
        ) : null}

        {!loading && registryItem && (
          <Box
            sx={{
              p: 2,
              borderTop: 1,
              borderColor: "divider",
              flexShrink: 0,
              backgroundColor: "background.paper",
            }}
          >
            <Button
              fullWidth
              variant="contained"
              onClick={() =>
                navigate(
                  `/rubiregistry/${orgId}/${registryItem.survey_class}/${registryItem.id}`,
                )
              }
            >
              View Full Entry
            </Button>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default RegistryDetailsPanel;
